import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

/**
 * Reusable N-box one-time-code input used across the 2FA screens.
 * Presentational only: emits the joined string via onChange.
 *
 * When `autoFocus` is set the first empty box is focused on mount so the user
 * can start typing the code without clicking into the control.
 */
const CodeInput = ({
  length, value, onChange, hasError, disabled, autoFocus,
}) => {
  const inputsRef = useRef([]);

  const digits = Array.from({ length }, (_, i) => value[i] || '');

  useEffect(() => {
    if (!autoFocus || disabled) return;
    // Focus the first empty box (or the last box if the code is already full).
    const firstEmpty = digits.findIndex((d) => !d);
    const target = firstEmpty === -1 ? length - 1 : firstEmpty;
    const el = inputsRef.current[target];
    if (el) el.focus();
    // Only run on mount; re-focusing on every value change would fight the
    // per-keystroke focus advancement in handleChange.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const focusInput = (index) => {
    const el = inputsRef.current[index];
    if (el) el.focus();
  };

  const setDigit = (index, digit) => {
    const next = digits.slice();
    next[index] = digit;
    onChange(next.join(''));
  };

  const handleChange = (index, e) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (!raw) {
      setDigit(index, '');
      return;
    }
    // Take the last typed character (handles overwrite of a filled box)
    const digit = raw.slice(-1);
    setDigit(index, digit);
    if (index < length - 1) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      focusInput(index - 1);
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      focusInput(index - 1);
    }
    if (e.key === 'ArrowRight' && index < length - 1) {
      focusInput(index + 1);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, length);
    if (pasted) {
      onChange(pasted);
      focusInput(Math.min(pasted.length, length - 1));
    }
  };

  return (
    <div className="tf-code">
      {digits.map((digit, index) => (
        <input
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          ref={(el) => { inputsRef.current[index] = el; }}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          value={digit}
          disabled={disabled}
          className={cn({ 'tf-code__box--error': hasError })}
          onChange={(e) => handleChange(index, e)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          aria-label={`Digit ${index + 1}`}
        />
      ))}
    </div>
  );
};

CodeInput.propTypes = {
  length: PropTypes.number,
  value: PropTypes.string,
  onChange: PropTypes.func,
  hasError: PropTypes.bool,
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
};

CodeInput.defaultProps = {
  length: 6,
  value: '',
  onChange: () => {},
  hasError: false,
  disabled: false,
  autoFocus: false,
};

export default CodeInput;
